import { API_CONFIG, createApiUrl } from './api';

class ApiError extends Error {
  status: number;
  body?: any;
  
  constructor(status: number, message: string, body?: any) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

interface RequestOptions {
  requiresAuth?: boolean;
  headers?: Record<string, string>;
}

const makeRequest = async (url: string, options: RequestInit = {}): Promise<any> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.TIMEOUT);
  
  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorBody = await response.text();
      let errorMessage = `HTTP ${response.status}`;
      
      try {
        const parsedError = JSON.parse(errorBody);
        errorMessage = parsedError.message || parsedError.error || errorMessage;
      } catch {
        // If parsing fails, use the raw text or default message
        errorMessage = errorBody || errorMessage;
      }
      
      throw new ApiError(response.status, errorMessage, errorBody);
    }

    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return await response.json();
    }
    
    return null;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof ApiError) {
      throw error;
    }
    throw new Error(error instanceof Error ? error.message : 'Network error');
  }
};

const getAuthToken = (): string | null => {
  if (typeof window !== 'undefined') {
    return localStorage.getItem('accessToken');
  }
  return null;
};

export class ApiClient {
  /**
   * Make a GET request
   */
  static async get<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const url = endpoint.startsWith('http') ? endpoint : createApiUrl(endpoint);
    const headers: Record<string, string> = { ...options.headers };
    
    if (options.requiresAuth) {
      const token = getAuthToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    return await makeRequest(url, {
      method: 'GET',
      headers,
    });
  }

  /**
   * Make a POST request
   */
  static async post<T>(endpoint: string, data?: any, options: RequestOptions = {}): Promise<T> {
    const url = endpoint.startsWith('http') ? endpoint : createApiUrl(endpoint);
    const headers: Record<string, string> = { ...options.headers };
    
    if (options.requiresAuth) {
      const token = getAuthToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    return await makeRequest(url, {
      method: 'POST',
      headers,
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * Make a PUT request
   */
  static async put<T>(endpoint: string, data?: any, options: RequestOptions = {}): Promise<T> {
    const url = endpoint.startsWith('http') ? endpoint : createApiUrl(endpoint);
    const headers: Record<string, string> = { ...options.headers };
    
    if (options.requiresAuth) {
      const token = getAuthToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    return await makeRequest(url, {
      method: 'PUT',
      headers,
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * Make a DELETE request
   */
  static async delete<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const url = endpoint.startsWith('http') ? endpoint : createApiUrl(endpoint);
    const headers: Record<string, string> = { ...options.headers };
    
    if (options.requiresAuth) {
      const token = getAuthToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    return await makeRequest(url, {
      method: 'DELETE',
      headers,
    });
  }

  /**
   * Make a PATCH request
   */
  static async patch<T>(endpoint: string, data?: any, options: RequestOptions = {}): Promise<T> {
    const url = endpoint.startsWith('http') ? endpoint : createApiUrl(endpoint);
    const headers: Record<string, string> = { ...options.headers };
    
    if (options.requiresAuth) {
      const token = getAuthToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    return await makeRequest(url, {
      method: 'PATCH',
      headers,
      body: data ? JSON.stringify(data) : undefined,
    });
  }
}

export { ApiError };
export default ApiClient;